/*
 * PREFERENCE SELECTORS - getting data out of the preference store
 *
 * These routines require a "state" value from the preference store, to access
 * the data model from anywhere,use the preferenceStore module which will create the
 * state then call these routines
 */

import { createSelector } from 'reselect';

import { selectorsHelpers } from 'app-components/flux/core';
import type RootState from 'app-components/utils/redux-store/rootState';

import {
  type AccountPreferencesById,
  type AccountsPreferencesValue,
  type DatasetWebAppPreferences,
  PREFERENCES_REDUCER_KEY,
  type PreferenceGroup,
  type Preferences,
  type PreferencesLoadPending,
  type PreferencesStore,
  type SharedPreferences,
  type TransactionRegisterPreferences,
} from './preferencesTypes';
import { colorLookup } from './preferencesUtils';

type PreferencesState = Pick<RootState, typeof PREFERENCES_REDUCER_KEY>;

export function mapJSObject<V, R>(obj: { readonly [key: string]: V }, fn: (value: V) => R): { [key: string]: R } {
  const ret: { [key: string]: R } = {};
  Object.keys(obj).forEach((key) => {
    ret[key] = fn(obj[key]);
  });
  return ret;
}

/*
 * For these selectors, state is global state as it is provided to React (not specific to preferences)
 * We use them to select into the store and get data for this REDUCER_KEY
 */
export const getPreferenceStore = (state: PreferencesState): PreferencesStore => state[PREFERENCES_REDUCER_KEY];
export const getPreferences = (state: PreferencesState): Preferences => getPreferenceStore(state).preferences;
export const getError = (state: PreferencesState): unknown => getPreferenceStore(state).error;
export const getIsLoading = (state: PreferencesState): boolean | undefined => getPreferenceStore(state).isLoading;
export const getLoadPending = (state: PreferencesState): boolean =>
  getPreferenceStore(state).loadPending.shared.user || getPreferenceStore(state).loadPending.shared.dataset;
export const getLoadPendingState = (state: PreferencesState): PreferencesLoadPending =>
  getPreferenceStore(state).loadPending;

/*
 * The selectors below operate directly on the preferences datastore (getPreferenceStore)
 */

const isIndexable = (value: unknown): value is { readonly [key: string]: unknown } =>
  typeof value === 'object' && value !== null;

// Walks the path; a falsy step ends the walk with that value.
const pathIn = (obj: unknown, path: readonly string[]): unknown =>
  path.reduce<unknown>((current, key) => {
    if (!current) {
      return current;
    }
    return isIndexable(current) ? current[key] : undefined;
  }, obj);

type SharedPreferencesQuery = {
  readonly group?: PreferenceGroup;
  readonly path?: readonly string[];
};

// -------------------------------------------------
// getSharedPreferencesByPath
//
// Reads the shared preferences of a group ('user' or 'dataset'), or the value at a path of keys
// inside them. Without a group, the path starts with the group.
//
export function getSharedPreferencesByPath<G extends PreferenceGroup>(
  state: PreferencesState,
  query: { readonly group: G; readonly path?: undefined },
): SharedPreferences[G];
export function getSharedPreferencesByPath<G extends PreferenceGroup, K extends string>(
  state: PreferencesState,
  query: { readonly group: G; readonly path: readonly [K] },
): SharedPreferences[G][K];
export function getSharedPreferencesByPath(state: PreferencesState, query: SharedPreferencesQuery): unknown;
export function getSharedPreferencesByPath(state: PreferencesState, { group, path }: SharedPreferencesQuery): unknown {
  const prefs = getPreferences(state);
  if (group) {
    return path ? pathIn(prefs.shared[group], path) : prefs.shared[group];
  }
  // if no group,path is a  key
  if (path) {
    return pathIn(prefs.shared, path);
  }
  return null;
}

export const getDatasetPreferences = (state: PreferencesState): DatasetWebAppPreferences =>
  getPreferences(state).shared.dataset.webApp;

export function allAccountPreferences(state: PreferencesState): AccountsPreferencesValue | null {
  return getDatasetPreferences(state).accountsPrefs || null;
}

export function accountPreferencesById(state: PreferencesState): AccountPreferencesById | null | undefined {
  const acctPrefs = allAccountPreferences(state);
  if (acctPrefs === null) {
    return null;
  }
  return acctPrefs === 'clear' ? undefined : acctPrefs.byId;
}

type ThemeMode = 'dark' | 'light';
type MatchMedia = (query: string) => { readonly matches: boolean };

// Only browsers have matchMedia.
const hasMatchMedia = (scope: object): scope is { matchMedia: MatchMedia } =>
  'matchMedia' in scope && typeof scope.matchMedia === 'function';

const getSystemThemeMode = (): ThemeMode => {
  const scope: object = globalThis;
  return hasMatchMedia(scope) && scope.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

export const getWebThemes = createSelector(
  (state: PreferencesState): ThemeMode => (getDatasetPreferences(state).theme === 'ACME_DARK' ? 'dark' : 'light'),
  getSystemThemeMode, // TODO: would not trigger update dynamically
  selectorsHelpers.createValueCache((current: ThemeMode, system: ThemeMode) => ({ current, system })),
);

export const accountColorsById = createSelector(
  allAccountPreferences,
  (accountPrefs): { [accountId: string]: string | undefined } => {
    if (accountPrefs && accountPrefs !== 'clear' && accountPrefs.byId) {
      return mapJSObject(accountPrefs.byId, (item) => colorLookup(item?.color));
    }
    return {};
  },
);

// register preferences
const getRegisterPreferences = createSelector(
  getDatasetPreferences,
  (datasetPreferences): TransactionRegisterPreferences | undefined => datasetPreferences?.transactionRegister,
);
export const showFlags = createSelector(getRegisterPreferences, (registerPrefs): boolean | undefined =>
  registerPrefs?.regFieldsSort?.cashFlow?.includes?.('userFlag'),
);

export const getWatchlistSetupCompleted = createSelector(
  getDatasetPreferences,
  (datasetPreferences): boolean => !!datasetPreferences.watchlistSetupCompleted,
);

export const getHidePlanned = createSelector(getDatasetPreferences, (datasetPreferences): boolean =>
  Boolean(datasetPreferences.hidePlanned),
);

export const getSpendingPlanSetupCompleted = createSelector(getDatasetPreferences, (datasetPreferences): boolean =>
  datasetPreferences.spendingPlanSetupCompleted ? datasetPreferences.spendingPlanSetupCompleted : false,
);
